import type {
  IBallotLoadResult,
  IBallotSharedSave,
  IRatingProcessStepCommitteeMatrixBallot,
  IRatingProcessStepCommitteeMatrixBallotConsideration,
  IRatingProcessStepCommitteeMatrixBallotInstrument,
  IRatingProcessStepCommitteeMatrixBallotInstrumentStep,
  IRatingProcessStepCommitteeMatrixBallotScore,
  IRatingProcessStepCommitteeMatrixBallotSeries,
} from '__generated__';
import { ePERMISSIONS } from 'services/user-position';

/**
 * F-39 — the committee matrix follows the committee step: its edit rights are the view
 * permission (ballot GetMetaData `editPermission`), not `isAllowToEditRatingProcess`.
 */
export const COMMITTEE_MATRIX_EDIT_PERMISSION = ePERMISSIONS.isAllowToViewRatingProcesses;

/**
 * B-01 — Swagger types the ballot without its child rows; Load and SaveBallot carry them
 * (scores[], considerations[], instruments[].steps[]).
 */
export type BallotInstrumentWithSteps = IRatingProcessStepCommitteeMatrixBallotInstrument & {
  steps?: IRatingProcessStepCommitteeMatrixBallotInstrumentStep[] | null;
};

export type BallotWithDetails = IRatingProcessStepCommitteeMatrixBallot & {
  scores?: IRatingProcessStepCommitteeMatrixBallotScore[] | null;
  considerations?: IRatingProcessStepCommitteeMatrixBallotConsideration[] | null;
  instruments?: BallotInstrumentWithSteps[] | null;
  /** F-12 — the voter's vote per series (absent = same as the final rating). */
  series?: IRatingProcessStepCommitteeMatrixBallotSeries[] | null;
};

export type BallotLoad = Omit<IBallotLoadResult, 'ballots'> & {
  ballots: BallotWithDetails[] | null;
};

export type SaveBallotScore = {
  subParameterID: number;
  score: number | null;
  deviationJustification: string | null;
};

export type SaveBallotConsideration = {
  considerationID: number;
  notches: number;
  justification: string | null;
};

export type SaveBallotInstrumentStep = {
  instrumentStepID: number;
  notch: number;
  justification: string | null;
};

export type SaveBallotInstrument = {
  instrumentTrackID: number;
  midroogRatingID: number | null;
  midroogOutlookID: number | null;
  justification: string | null;
  steps: SaveBallotInstrumentStep[];
};

/** F-12 — midroogRatingID null = same as the voter's final rating. */
export type SaveBallotSeries = {
  seriesID: number;
  midroogRatingID: number | null;
  midroogOutlookID: number | null;
  justification: string | null;
};

/**
 * F-10 — one voter's ballot, saved by the operator (`userID` = the voter). Totals are computed
 * by the backend; any sent by the FE are ignored.
 */
export type SaveBallotBody = {
  userID: string;
  midroogOutlookID: number | null;
  remarks: string | null;
  votedMidroogRatingID: number | null;
  voteJustification: string | null;
  scores: SaveBallotScore[];
  considerations: SaveBallotConsideration[];
  instruments: SaveBallotInstrument[];
  series: SaveBallotSeries[];
};

export type SaveSharedBody = IBallotSharedSave;

/** Roster row with the names Load leaves out (user / committeePosition are null there). */
export type BallotRosterMember = {
  id: number;
  userID: string | null;
  userFullName: string | null;
  committeePositionID: number;
  committeePositionTitle: string | null;
  committeePositionIsVoting: boolean;
  votingOrder: number;
};

/** F-17 — a committee's matrix link, as the voting summary and the roster lock need it. */
export type BallotLinkRow = {
  id: number;
  ratingMatrixFrameworkTemplateID: number | null;
  isPrimary: boolean;
  chairLockedDate: string | null;
};
